/**
 * Platform L3 map custom views client (ADR-MV-05).
 * Absolute `/api/v1/customer-account/map-custom-views*` — NOT tenant ApiClient.
 * Transport: stormAwareFetch + platformJsonHeaders (NEVER invent fetchPlatformAccountJson).
 */

import {
  customerEnvelopeApiError,
  customerInvalidJsonApiError,
} from '../../shared/errors/customerApiParseErrors.js';
import { stormAwareFetch } from '../../shared/http/stormAwareFetch.js';
import { platformJsonHeaders } from '../../shared/http/platformEnvelopeLocale.js';
import type {
  CreateMapCustomViewBody,
  MapCustomViewDTO,
  PatchMapCustomViewBodyForKind,
} from './mapViewsTypes.js';

/** G-NOMINATIM — SSOT in mapViewsNominatim (enqueueNominatim + Pass|Fail empty/hits/error). */
export {
  searchNominatimPlaces,
  isPragueCdpNominatimHit,
  PLACE_SEARCH_EMPTY_TEST_ID,
  PLACE_SEARCH_EMPTY_I18N_KEY,
  PLACE_SEARCH_MULTI_HIT_TEST_ID,
  PLACE_SEARCH_MULTI_HIT_I18N_KEY,
} from './mapViewsNominatim.js';
export type {
  NominatimPlaceHit,
  SearchNominatimPlacesResult,
} from './mapViewsNominatim.js';

const VIEWS_PATH = '/api/v1/customer-account/map-custom-views';

interface SuccessEnvelope<T> {
  success: true;
  data: T;
}

interface ErrorEnvelope {
  success: false;
  error: string;
  code?: string;
}

export interface MapViewsClientAuth {
  apiBaseUrl: string;
  signal?: AbortSignal;
}

type DecimalParse =
  | { readonly ok: true; readonly value: number | null }
  | { readonly ok: false };

/**
 * EV-G2-NORMALIZE Decimal — blank/whitespace → unparseable **before** `Number()`
 * (never `Number('')`→0 / whitespace-as-0). Finite number|trimmed string → value;
 * `null` → null; else unparseable.
 */
function parseDecimalField(raw: unknown): DecimalParse {
  if (raw === null) {
    return { ok: true, value: null };
  }
  if (typeof raw === 'number') {
    return Number.isFinite(raw) ? { ok: true, value: raw } : { ok: false };
  }
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.length === 0) {
      return { ok: false };
    }
    const n = Number(trimmed);
    return Number.isFinite(n) ? { ok: true, value: n } : { ok: false };
  }
  return { ok: false };
}

/**
 * Opposite-kind / nullable Decimal: unparseable → field-null (never leave string/NaN).
 * Kind-required uses {@link requireFiniteDecimal} for row-null.
 */
function optionalDecimalOrNull(raw: unknown): number | null {
  const parsed = parseDecimalField(raw);
  return parsed.ok ? parsed.value : null;
}

function requireFiniteDecimal(raw: unknown): number | null {
  const parsed = parseDecimalField(raw);
  if (!parsed.ok || parsed.value === null) {
    return null;
  }
  return parsed.value;
}

function isMapCustomViewKind(
  value: unknown,
): value is MapCustomViewDTO['kind'] {
  return (
    value === 'around_me' ||
    value === 'around_place' ||
    value === 'map_camera' ||
    value === 'exact_bounds'
  );
}

function normalizePlaceLabel(raw: unknown): string | null {
  if (raw === null || raw === undefined) {
    return null;
  }
  if (typeof raw === 'string') {
    return raw;
  }
  return null;
}

/**
 * EV-G2-NORMALIZE — peer `normalizeSalesPointContactItem(raw: unknown)`.
 * Pass: blank/whitespace → unparseable (never ''→0); Decimal via trim + Number + Number.isFinite;
 * field-null OK on nullable/opposite-kind; kind→geo SSOT row-keep; around_place.placeLabel null = keep.
 * Pass: kind-required geometry (SSOT) unparseable|null → whole-row drop (return null).
 * Fail: leave string/NaN; accept '' / whitespace-only strings as 0; Wire-typed-only; soft-keep bad required geometry;
 * drop row solely because placeLabel is null after clear.
 */
export function normalizeMapCustomView(raw: unknown): MapCustomViewDTO | null {
  if (raw === null || typeof raw !== 'object') {
    return null;
  }
  const item = raw as Record<string, unknown>;
  if (!isMapCustomViewKind(item.kind)) {
    return null;
  }
  const kind = item.kind;
  if (typeof item.id !== 'number' || !Number.isFinite(item.id)) {
    return null;
  }
  if (typeof item.name !== 'string') {
    return null;
  }
  if (typeof item.createdAt !== 'string' || typeof item.updatedAt !== 'string') {
    return null;
  }

  const placeLabel = normalizePlaceLabel(item.placeLabel);

  if (kind === 'around_me' || kind === 'around_place') {
    const radiusKm = requireFiniteDecimal(item.radiusKm);
    const centerLat = requireFiniteDecimal(item.centerLat);
    const centerLng = requireFiniteDecimal(item.centerLng);
    if (radiusKm === null || centerLat === null || centerLng === null) {
      return null;
    }
    return {
      id: item.id,
      kind,
      name: item.name,
      radiusKm,
      centerLat,
      centerLng,
      placeLabel,
      swLat: optionalDecimalOrNull(item.swLat),
      swLng: optionalDecimalOrNull(item.swLng),
      neLat: optionalDecimalOrNull(item.neLat),
      neLng: optionalDecimalOrNull(item.neLng),
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    };
  }

  // map_camera | exact_bounds — four corners required (EV-G2-NORMALIZE SSOT)
  const swLat = requireFiniteDecimal(item.swLat);
  const swLng = requireFiniteDecimal(item.swLng);
  const neLat = requireFiniteDecimal(item.neLat);
  const neLng = requireFiniteDecimal(item.neLng);
  if (swLat === null || swLng === null || neLat === null || neLng === null) {
    return null;
  }
  return {
    id: item.id,
    kind,
    name: item.name,
    radiusKm: optionalDecimalOrNull(item.radiusKm),
    centerLat: optionalDecimalOrNull(item.centerLat),
    centerLng: optionalDecimalOrNull(item.centerLng),
    placeLabel,
    swLat,
    swLng,
    neLat,
    neLng,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  };
}

/**
 * EV-G2-NORMALIZE — peer `normalizeSalesPointContactsList`.
 * Input SSOT: bare GET `data` array **after** envelope unwrap (GET client:
 * parse envelope → `body.data` → then this; never pass full `{success,data}`).
 * Pass: `!Array.isArray(data)` → `[]`; each row via `normalizeMapCustomView`;
 * invalid/missing `kind` → row-null → filter out.
 * Fail: cast non-array `data` to `MapCustomViewDTO[]`.
 */
export function normalizeMapCustomViews(data: unknown): MapCustomViewDTO[] {
  if (!Array.isArray(data)) {
    return [];
  }
  const out: MapCustomViewDTO[] = [];
  for (const row of data) {
    const normalized = normalizeMapCustomView(row);
    if (normalized !== null) {
      out.push(normalized);
    }
  }
  return out;
}

function platformUrl(apiBaseUrl: string, path: string): string {
  const base = apiBaseUrl.replace(/\/+$/, '');
  return `${base}${path}`;
}

function authFetchInit(init: {
  method: string;
  headers?: Record<string, string>;
  body?: string;
  signal?: AbortSignal;
}): RequestInit {
  const headers: Record<string, string> = platformJsonHeaders(init.headers ?? {});
  return {
    method: init.method,
    headers,
    credentials: 'include',
    ...(init.body !== undefined ? { body: init.body } : {}),
    ...(init.signal !== undefined ? { signal: init.signal } : {}),
  };
}

async function parseEnvelope<T>(response: Response): Promise<T> {
  let body: SuccessEnvelope<T> | ErrorEnvelope;
  try {
    body = (await response.json()) as SuccessEnvelope<T> | ErrorEnvelope;
  } catch {
    throw customerInvalidJsonApiError(response.status);
  }

  if (!response.ok || body.success !== true) {
    throw customerEnvelopeApiError(response.status, body);
  }

  return body.data;
}

/** GET `/api/v1/customer-account/map-custom-views` — list createdAt ASC. */
export async function listMapCustomViews(
  input: MapViewsClientAuth,
): Promise<MapCustomViewDTO[]> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, VIEWS_PATH),
    authFetchInit({
      method: 'GET',
      signal: input.signal,
    }),
  );
  const data = await parseEnvelope<unknown>(response);
  return normalizeMapCustomViews(data);
}

/** POST `/api/v1/customer-account/map-custom-views` */
export async function createMapCustomView(
  input: MapViewsClientAuth & { body: CreateMapCustomViewBody },
): Promise<MapCustomViewDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, VIEWS_PATH),
    authFetchInit({
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  const data = await parseEnvelope<unknown>(response);
  const normalized = normalizeMapCustomView(data);
  if (normalized === null) {
    throw customerInvalidJsonApiError(response.status);
  }
  return normalized;
}

/**
 * EV-G1-PATCH-KIND — ONE path only. Do NOT export untagged `PatchMapCustomViewBody`.
 * Require: patchMapCustomView<K>(…, body: PatchMapCustomViewBodyForKind<K>) with persisted kind K.
 * EV-G9-PATCH-TUPLE: incomplete **tuple** geo keys → **422**; `placeLabel` is **not** a tuple trigger;
 * placeLabel-only → **200** only on `around_place`; placeLabel on non-`around_place` → **422**;
 * name-only → **200** (EV-G7-PATCH-EMPTY).
 * Empty `{}` / unrecognized-keys-only → **422** (EV-G7-PATCH-EMPTY).
 */
export async function patchMapCustomView<K extends MapCustomViewDTO['kind']>(
  input: MapViewsClientAuth & {
    id: number;
    body: PatchMapCustomViewBodyForKind<K>;
  },
): Promise<MapCustomViewDTO> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${VIEWS_PATH}/${String(input.id)}`),
    authFetchInit({
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input.body),
      signal: input.signal,
    }),
  );
  const data = await parseEnvelope<unknown>(response);
  const normalized = normalizeMapCustomView(data);
  if (normalized === null) {
    throw customerInvalidJsonApiError(response.status);
  }
  return normalized;
}

/** DELETE `/api/v1/customer-account/map-custom-views/:id` — ownership miss → 404. */
export async function deleteMapCustomView(
  input: MapViewsClientAuth & { id: number },
): Promise<void> {
  const response = await stormAwareFetch(
    platformUrl(input.apiBaseUrl, `${VIEWS_PATH}/${String(input.id)}`),
    authFetchInit({
      method: 'DELETE',
      signal: input.signal,
    }),
  );
  await parseEnvelope<unknown>(response);
}
